import { call, put, select, takeLatest } from 'redux-saga/effects'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { getServiceIdSelector } from '../../user/selectors'
import { getPcrIdSelector } from '../../home/selectors'
import { IPcr } from '../../pcr/store/pcr.types'
import { getCadImportSelector } from '../selectors'
import { CadImportedDataFlags, CadRecordDataPoint, ICadImportState } from '../store/cadImport.types'
import { CadImportConstants } from '../store/cadImport.constants'
import { setSaveCadDataResponse } from '../store/cadImport.actions'
import { IMergedDateTimeConfig, ITimesState } from '../../times/store/times.types'
import { getTimesSelector } from '../../times/selectors'
import { getAgeFromReference, getAgeUnitOptionIdFromReference } from '../../../utilities/dateTime.util'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { getPatientDemographicsOptionsSelector } from '../../options/selectors/patientDemographicsOptions.selector'
import { IHospitalOption, IPatientDemographicsOptionsState, IPickupAddressOptionsState, ITripInformationOptionsState, IUnitOption } from '../../options/store/options.types'
import { IOption } from '../../../common/common.types'
import { IDispositionState } from '../../destinationAddress/store/destinationAddress.types'
import { getDestinationAddressSelector } from '../../destinationAddress/selectors/destinationAddress.selectors'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { IConfigurationsState, IDataPointsConfig, IServiceConfig } from '../../configurations/store/configurations.types'
import { getTripInformationOptionsSelector } from '../../options/selectors/tripInformationOptions.selector'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { clearDestinationAddressLocationData, clearPickupAddressLocationData, hasRecLocationChanged, hasRefLocationChanged, isValidLocationObj, transformCADData, updateNemsis35Disposition } from '../../../utilities/cad.util'
import { setHasPatient } from '../../patientInformation/store/patientInformation.actions'
import { ICommonAddressesOption } from '../../pickupAddress/store/pickupAddress.types'
import { setCommonAddressData as setRefCommonAddressData } from '../../pickupAddress/saga/setRefCommonAddressValues.saga'
import { setCommonAddressData as setRecCommonAddressData } from '../../destinationAddress/saga/setRecCommonAddressValues.saga'
import { setHospitalDemographicData as setRecHospitalDemographicData } from '../../destinationAddress/saga/setRecHospitalDemographicValues.saga'
import { setHospitalDemographicData as setRefHospitalDemographicData } from '../../pickupAddress/saga/setRefHospitalDemographicValues.saga'
import { getResponseMode, isAirUnit, optionwWithStateIds } from '../../tripInformation/utils/tripInformation.util'
import { getPickupAddressOptionsSelector } from '../../options/selectors/pickupAddressOptions.selector'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { getMergedDateTimeConfig } from '../../times/saga/setDateTimeConfigs.saga'
import { capitalizeFirstChar, setDataToObject } from '../../../utilities/data.util'
import { DateTimeConfig } from '../../times/store/times.constants'
import { setTimes } from '../../times/store/times.actions'
import { addTimesItem } from '../../timeline/saga/addTimesItem.saga'
import { ITimelineState } from '../../timeline/store/timeline.types'
import { getPatientTimelineSelector } from '../../timeline/selectors/patientTimeline.selector'
import { setCadComments } from '../../sceneDescription/store/sceneDescription.actions'
import { showAtLanding, showDescent } from '../../times/utils/timesGrid.util'

const getCustomLabel = (fieldName: string, timesUpdated: ITimesState): string | null => {
  const isCustomField = fieldName.toLowerCase().startsWith('custom')
  if (!isCustomField) {
    return null
  }
  const index = fieldName.slice(6)
  const labelKey = `custom${index}DateTimeLabel` as keyof ITimesState
  return timesUpdated[labelKey] as string | null
}

export const UnhideReadOnlyDateTimeFields = (
  importedCadData: CadRecordDataPoint[],
  mergedConfig: IMergedDateTimeConfig[],
  timesUpdated: ITimesState
): ITimesState => {
  let updated = { ...timesUpdated }
  
  const timesCadDataMap = new Map<string, CadRecordDataPoint>()
  importedCadData.forEach((dataPoint) => {
    if (dataPoint.appStateLocation.startsWith('times.')) {
      timesCadDataMap.set(dataPoint.appStateLocation, dataPoint)
    }
  })
  
  mergedConfig.forEach(({ fieldName, configValue, disableValue }) => {
    const isCustomField = fieldName.toLowerCase().startsWith('custom')
    const customIndex = isCustomField ? fieldName.slice(6) : null
    
    if (isCustomField && customIndex) {
      const customLabel = getCustomLabel(fieldName, timesUpdated)
      const hasCustomLabel = customLabel != null && customLabel.trim() !== ''
      if (!hasCustomLabel) {
        return
      }
    }
    
    const dateValue = timesCadDataMap.get(`times.${fieldName}Date`)?.value
    const timeValue = timesCadDataMap.get(`times.${fieldName}Time`)?.value
    const cadValue = dateValue || timeValue

    if (configValue !== DateTimeConfig.HIDE_DATE_TIME) {
      const hideKey = (isCustomField && customIndex)
        ? (`__hideCustom${customIndex}DateTime` as keyof ITimesState)
        : (`__hide${capitalizeFirstChar(fieldName)}` as keyof ITimesState)
      if (cadValue) {
        updated = setDataToObject(updated, hideKey, false)
      } else {
        updated = setDataToObject(updated, hideKey, disableValue === 1)
      }
    }
  })

  return updated
}

const skipImportForHiddenTimeFields = (
  dataPoint: CadRecordDataPoint,
  mergedConfig: IMergedDateTimeConfig[],
  times: ITimesState
): boolean => {
  if (!dataPoint.appStateLocation.startsWith('times.')) return false
  const fieldName = dataPoint.appStateLocation.replace(/^times\.|Date|Time/g, '')
  const fieldConfig = mergedConfig.find(f => f.fieldName === fieldName)
  
  const isCustomField = fieldName.toLowerCase().startsWith('custom')
  if (isCustomField) {
    const index = fieldName.slice(6)
    const labelKey = `custom${index}DateTimeLabel` as keyof ITimesState
    const customLabel = times[labelKey] as string | null
    const hasCustomLabel = customLabel != null && customLabel.trim() !== ''
    if (!hasCustomLabel) {
      return true
    }
  }
  
  return fieldConfig?.configValue === DateTimeConfig.HIDE_DATE_TIME
}

const skipImportForHiddenIFRField = (
  dataPoint: CadRecordDataPoint,
  datapointsConfig: IDataPointsConfig,
  unit: IUnitOption | null
): boolean => {
  if (dataPoint.id !== CadImportConstants.IFR) return false
  if (!isAirUnit(unit?.unitType ?? null)) return true
  if (dataPoint.id === CadImportConstants.IFR) return datapointsConfig.DP_IFR !== 1
  return datapointsConfig.DP_MAXALT !== 1 || unit?.showMaxAlt !== 1
}

export function* saveCadData() {
  const cadImport: ICadImportState = yield select(getCadImportSelector)
  const { importedCadData } = cadImport
  const times: ITimesState = yield select(getTimesSelector)
  const disposition: IDispositionState = yield select(getDestinationAddressSelector)
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrID: string = yield select(getPcrIdSelector)
  const patientDemographicsOptions: IPatientDemographicsOptionsState = yield select(getPatientDemographicsOptionsSelector)
  const serviceConfig: IServiceConfig = yield select(getServiceConfigSelector)
  const { PR_NEMSIS35_DISPOSITION, PR_DISP_FIELDS, PR_PTCAT_DISP_MAP, PR_DISPATCHED_AS } = serviceConfig
  const configuration: IConfigurationsState = yield select(getConfigurationsSelector)
  const { commandFacilityConfig, datapointsConfig} = configuration
  
  const tripInformationOptions: ITripInformationOptionsState = yield select(getTripInformationOptionsSelector)
  const { emdPerformedOptions, dispatchedAsOptions, emsVehicleOptions, emsRegTailNumberOptions } = tripInformationOptions
  const pickupAddressOptionsState: IPickupAddressOptionsState = yield select(getPickupAddressOptionsSelector)
  const { responseModeOptions, responseModeCustomOptions } = pickupAddressOptionsState
  const cadImportedDataFlags: CadImportedDataFlags = { 
    hasAge: false, 
    hasPatientCategory: false
  }

  if (pcrID) {
    try {
      const mergedConfig = getMergedDateTimeConfig(serviceConfig, commandFacilityConfig)

      const pcrDocsService = new PcrDocsService(serviceId)
      const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)

      if (hasRefLocationChanged(pcrDoc, importedCadData)) {
        clearPickupAddressLocationData(pcrDoc)
      }

      if (hasRecLocationChanged(pcrDoc, importedCadData)) {
        clearDestinationAddressLocationData(pcrDoc)
      }

      // Unhide the Date / Time fields if they were hidden (due to Read-only clause) and CAD data exists for them
      const updatedTimes = UnhideReadOnlyDateTimeFields(importedCadData, mergedConfig, times)
      const pcrDocUpdated: any = { ...pcrDoc, times: updatedTimes, disposition }
      const { ageUnitOptions } = patientDemographicsOptions

      const cadUnit = importedCadData.find((d: CadRecordDataPoint) => d.id === 'UNIT')?.value
      const effectiveUnit: IUnitOption | null = cadUnit ?? pcrDoc.patient?.tripInformation?.unit ?? null

      importedCadData.map((dataPoint: CadRecordDataPoint, index) => {
        if (!PR_DISP_FIELDS && dataPoint.id === CadImportConstants.EMD_CODE) {
          return null
        }
        if (skipImportForHiddenTimeFields(dataPoint, mergedConfig, times)) {
          return null
        }
        if (skipImportForHiddenIFRField(dataPoint, datapointsConfig, effectiveUnit)) {
          return null
        }
        transformCADData(importedCadData, dataPoint, index, cadImportedDataFlags)

        const keys = dataPoint.appStateLocation.split('.')
        if (keys.length === 1) {
          return (pcrDocUpdated[keys[0]] = dataPoint.value)
        } else if (keys.length === 2) {
          if (pcrDocUpdated[keys[0]]) {
            return (pcrDocUpdated[keys[0]][keys[1]] = dataPoint.value)
          } else {
            pcrDocUpdated[keys[0]] = {}
            return (pcrDocUpdated[keys[0]][keys[1]] = dataPoint.value)
          }
        } else if (keys.length === 3) {
          if (pcrDocUpdated[keys[0]] && pcrDocUpdated[keys[0]][keys[1]]) {
            return (pcrDocUpdated[keys[0]][keys[1]][keys[2]] = dataPoint.value)
          } else if (pcrDocUpdated[keys[0]]) {
            pcrDocUpdated[keys[0]][keys[1]] = {}
            return (pcrDocUpdated[keys[0]][keys[1]][keys[2]] = dataPoint.value)
          } else {
            pcrDocUpdated[keys[0]] = {}
            pcrDocUpdated[keys[0]][keys[1]] = {}
            return (pcrDocUpdated[keys[0]][keys[1]][keys[2]] = dataPoint.value)
          }
        }
      })

      if (pcrDocUpdated.patient?.patientDemographics?.dOB) {
        const dob = pcrDocUpdated.patient.patientDemographics.dOB
        const dispatchedDate = pcrDocUpdated.times?.dispatchedDate ?? times.dispatchedDate ?? null
        pcrDocUpdated.patient.patientDemographics.age = getAgeFromReference(dob, dispatchedDate)
        pcrDocUpdated.patient.patientDemographics.ageUnit = ageUnitOptions.find((option: { id: IOption }) => option.id === getAgeUnitOptionIdFromReference(dob, dispatchedDate))
      }

      if (PR_DISP_FIELDS && pcrDocUpdated?.patient?.tripInformation?.emd && pcrDocUpdated?.patient?.tripInformation?.emd !== '') {
        const emdPerformedOption = emdPerformedOptions?.find((option) => option.id === 570)
        pcrDocUpdated.patient.tripInformation.emdPerformed = emdPerformedOption
      }

      if(cadImportedDataFlags.hasPatientCategory && PR_PTCAT_DISP_MAP > 0 && PR_DISPATCHED_AS > 0) {
        const patientCategoryOption = pcrDocUpdated.patient.tripInformation.patientCategory
        if (patientCategoryOption && PR_PTCAT_DISP_MAP > 0 && PR_DISPATCHED_AS > 0) {
          const isOptionInList = dispatchedAsOptions.some((data) => data.id === patientCategoryOption.id)
          if (isOptionInList) {
            pcrDocUpdated.patient.tripInformation.dispatchedAs = patientCategoryOption
          }
        }
      }

      const getResponseModeOptions = () => {
        if (serviceConfig.PR_REFREC_MODE === 2 && responseModeCustomOptions?.length > 0) {
          return responseModeCustomOptions
        }
        return responseModeOptions
      }

      if (pcrDocUpdated?.patient?.tripInformation?.dispatchPriority) {       
        const responseModeOption = getResponseMode(pcrDocUpdated?.patient?.tripInformation?.dispatchPriority?.emergency || 0, getResponseModeOptions(), pcrDocUpdated?.patient?.tripInformation?.unit?.lights || null)
        pcrDocUpdated.patient.pickupAddress.responseMode = responseModeOption
      }

      if (PR_NEMSIS35_DISPOSITION === 1) {
        updateNemsis35Disposition(pcrDocUpdated, tripInformationOptions, updatedTimes, importedCadData)
      }

      const recData: ICommonAddressesOption | IHospitalOption | undefined = importedCadData.find((item: any) => item.id == 'REC')?.value

      if (recData && isValidLocationObj(recData)) {
        const isRecTypeHospital = pcrDocUpdated.disposition?.typeValue?.id === 2
        const isRecTypeOther = pcrDocUpdated.disposition?.typeValue?.id === 0
        if (isRecTypeOther)
          yield call(setRecCommonAddressData, recData as ICommonAddressesOption, pcrDocUpdated as IPcr)
        else if (isRecTypeHospital)
          yield call(setRecHospitalDemographicData, recData as IHospitalOption, pcrDocUpdated as IPcr)
      }

      const refData: ICommonAddressesOption | IHospitalOption | undefined = importedCadData.find((item: any) => item.id == 'REF')?.value

      if (refData && isValidLocationObj(refData)) {
        const isRefTypeHospital = pcrDocUpdated.patient?.pickupAddress?.typeValue?.id === 2
        const isRefTypeOther = pcrDocUpdated.patient?.pickupAddress?.typeValue?.id === 0
        if (isRefTypeOther)
          yield call(setRefCommonAddressData, refData as ICommonAddressesOption, pcrDocUpdated as IPcr)
        else if (isRefTypeHospital)
          yield call(setRefHospitalDemographicData, refData as IHospitalOption, pcrDocUpdated as IPcr)
      }

      const cadHasPatientData = importedCadData.length && importedCadData?.some(item => item?.id?.startsWith('PT_'))
      if (cadHasPatientData) yield put(setHasPatient(true))

      const cadUnitDataPoint = importedCadData.find((d: CadRecordDataPoint) => d.id === 'UNIT')
      if (cadUnitDataPoint?.value) {
        pcrDocUpdated.patient.tripInformation.unit = optionwWithStateIds(cadUnitDataPoint.value, emsVehicleOptions)
      }

      const cadRegTailDataPoint = importedCadData.find((d: CadRecordDataPoint) => d.id === 'UNIT_REGISTRATION')
      if (cadRegTailDataPoint?.value) {
        pcrDocUpdated.patient.tripInformation.registrationTailNumber = optionwWithStateIds(
          cadRegTailDataPoint.value,
          emsRegTailNumberOptions
        )
      }

      const cadCommentsDataPoint = importedCadData.find((d: CadRecordDataPoint) => d.id === 'COMMENTS')
      if (cadCommentsDataPoint) {
        const cadCommentsValue = (cadCommentsDataPoint.value || '').substring(0, 1000)
        yield put(setCadComments(cadCommentsValue))
      }

      const importedUnit = pcrDocUpdated.patient?.tripInformation?.unit as IUnitOption | null
      if (!showDescent(importedUnit, serviceConfig.AIR)) {
        pcrDocUpdated.times.descentDate = ''
        pcrDocUpdated.times.descentTime = ''
      }
      if (!showAtLanding(importedUnit)) {
        pcrDocUpdated.times.atLandingDate = ''
        pcrDocUpdated.times.atLandingTime = ''
      }

      yield put(setTimes(pcrDocUpdated.times))
      yield call(addTimesItem, true)
      const timeline: ITimelineState = yield select(getPatientTimelineSelector)

      pcrDocUpdated.patient.timelineItems = [...timeline.items]

      const response: string = yield call([pcrDocsService, pcrDocsService.savePCR], pcrDocUpdated)
      yield put(setSaveCadDataResponse(response))
    } catch (e) {
      yield put(throwPcrException(`saveCadData() error: ${e}`))
    }
  } else {
    // todo: add to audit log
  }
}

export function* saveCadDataSaga() {
  yield takeLatest([CadImportConstants.IMPORT_CAD_DATA], performWithMutexHOG, saveCadData)
}
